import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

import { Badge } from '@/components/ui/badge';
import { Main } from '@/components/layout/main';
import { PageHeader } from '@/components/layout/page-header';

import { ConfirmDialog } from '@/components/confirm-dialog';
import {
  getChatbotConfiguration,
  updateChatbotConfiguration,
  resetChatbotConfiguration
} from '@/api/chatbot';
import { handleServerError } from '@/utils/handle-server-error';
import type { ChatbotConfiguration, ChatbotConfigurationUpdate } from '@/types/chatbot';
import {
  Bot,
  Settings,
  RefreshCw,
  Save,
  RotateCcw,
  AlertTriangle,

} from 'lucide-react';

export default function ChatbotConfig() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<ChatbotConfiguration | null>(null);
  const [formData, setFormData] = useState<ChatbotConfigurationUpdate>({});
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load initial data
  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const data = await getChatbotConfiguration();
      setConfig(data);
      setFormData({
        model_name: data.model_name,
        temperature: data.temperature,
        max_tokens: data.max_tokens,
        max_iterations: data.max_iterations,
        system_prompt: data.system_prompt,
        doc_analysis_model_name: data.doc_analysis_model_name,
        doc_analysis_temperature: data.doc_analysis_temperature,
        doc_analysis_max_tokens: data.doc_analysis_max_tokens,
        doc_analysis_max_text_length: data.doc_analysis_max_text_length,
        doc_analysis_system_prompt: data.doc_analysis_system_prompt,
        max_conversation_history: data.max_conversation_history,
        conversation_timeout_hours: data.conversation_timeout_hours,
        max_message_length: data.max_message_length,
        max_audio_duration: data.max_audio_duration,
        default_message: data.default_message,
        is_active: data.is_active,
        is_default: data.is_default
      });
      setHasChanges(false);
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ChatbotConfigurationUpdate, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updatedConfig = await updateChatbotConfiguration(formData);
      setConfig(updatedConfig);
      setHasChanges(false);
      // Show success message
    } catch (error) {
      handleServerError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      const resetConfig = await resetChatbotConfiguration();
      setConfig(resetConfig);
      setFormData({
        model_name: resetConfig.model_name,
        temperature: resetConfig.temperature,
        max_tokens: resetConfig.max_tokens,
        max_iterations: resetConfig.max_iterations,
        system_prompt: resetConfig.system_prompt,
        doc_analysis_model_name: resetConfig.doc_analysis_model_name,
        doc_analysis_temperature: resetConfig.doc_analysis_temperature,
        doc_analysis_max_tokens: resetConfig.max_tokens,
        doc_analysis_max_text_length: resetConfig.doc_analysis_max_text_length,
        doc_analysis_system_prompt: resetConfig.doc_analysis_system_prompt,
        max_conversation_history: resetConfig.max_conversation_history,
        conversation_timeout_hours: resetConfig.conversation_timeout_hours,
        max_message_length: resetConfig.max_message_length,
        max_audio_duration: resetConfig.max_audio_duration,
        default_message: resetConfig.default_message,
        is_active: resetConfig.is_active,
        is_default: resetConfig.is_default
      });
      setHasChanges(false);
      setIsResetDialogOpen(false);
    } catch (error) {
      handleServerError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    loadConfiguration();
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Main>

          <div className="flex-1 min-w-0 space-y-4 p-4 md:p-8 pt-6">
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin" />
            </div>
          </div>
        </Main>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Main>

        <div className="flex-1 min-w-0 space-y-4 p-4 md:p-8 pt-6">
          <PageHeader
            leading={
              <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Bot className="h-5 w-5" />
              </span>
            }
            title={t('chatbotConfig.title')}
            description={t('chatbotConfig.description')}
            actions={
              <>
                <Button variant="outline" onClick={handleRefresh} disabled={saving}>
                  <RefreshCw className="h-4 w-4 me-2" />
                  {t('refresh')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsResetDialogOpen(true)}
                  disabled={saving}
                >
                  <RotateCcw className="h-4 w-4 me-2" />
                  {t('chatbotConfig.resetToDefaults')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                >
                  <Save className="h-4 w-4 me-2" />
                  {saving ? t('saving') : t('save')}
                </Button>
              </>
            }
          />

          {/* Configuration Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bot className="h-5 w-5 mr-2" />
                {t('chatbotConfig.status')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Label>{t('chatbotConfig.active')}:</Label>
                  <Badge variant={config?.is_active ? "default" : "secondary"}>
                    {config?.is_active ? t('active') : t('inactive')}
                  </Badge>
                </div>
                <div className="flex items-center space-x-2">
                  <Label>{t('chatbotConfig.default')}:</Label>
                  <Badge variant={config?.is_default ? "default" : "outline"}>
                    {config?.is_default ? t('yes') : t('no')}
                  </Badge>
                </div>
                <div className="flex items-center space-x-2">
                  <Label>{t('lastUpdated')}:</Label>
                  <span className="text-sm text-muted-foreground">
                    {config?.updated_at ? new Date(config.updated_at).toLocaleString() : '-'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Model Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                {t('chatbotConfig.modelConfiguration')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="model_name">{t('chatbotConfig.modelName')}</Label>
                  <Input
                    id="model_name"
                    value={formData.model_name || ''}
                    onChange={(e) => handleInputChange('model_name', e.target.value)}
                    placeholder="openai/gpt-4o"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.modelNameDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_tokens">{t('chatbotConfig.maxTokens')}</Label>
                  <Input
                    id="max_tokens"
                    type="number"
                    min="50"
                    max="4000"
                    value={formData.max_tokens || ''}
                    onChange={(e) => handleInputChange('max_tokens', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.maxTokensDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="temperature">
                    {t('chatbotConfig.temperature')}: {formData.temperature?.toFixed(1) || '0.0'}
                  </Label>
                  <input
                    id="temperature"
                    type="range"
                    min={0}
                    max={2}
                    step={0.1}
                    value={formData.temperature || 0}
                    onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.temperatureDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_iterations">{t('chatbotConfig.maxIterations')}</Label>
                  <Input
                    id="max_iterations"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.max_iterations || ''}
                    onChange={(e) => handleInputChange('max_iterations', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.maxIterationsDescription')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Document Analysis Model */}
          <Card>
            <CardHeader>
              <CardTitle>{t('chatbotDocAnalysis.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">{t('chatbotDocAnalysis.description')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="doc_analysis_model_name">{t('chatbotDocAnalysis.modelName')}</Label>
                  <Input
                    id="doc_analysis_model_name"
                    value={formData.doc_analysis_model_name || ''}
                    onChange={(e) => handleInputChange('doc_analysis_model_name', e.target.value)}
                    placeholder="openai/gpt-4o-mini"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotDocAnalysis.modelNameDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="doc_analysis_max_tokens">{t('chatbotDocAnalysis.maxTokens')}</Label>
                  <Input
                    id="doc_analysis_max_tokens"
                    type="number"
                    min="50"
                    max="4000"
                    value={formData.doc_analysis_max_tokens || ''}
                    onChange={(e) => handleInputChange('doc_analysis_max_tokens', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotDocAnalysis.maxTokensDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="doc_analysis_temperature">
                    {t('chatbotDocAnalysis.temperature')}: {formData.doc_analysis_temperature?.toFixed(1) || '0.0'}
                  </Label>
                  <input
                    id="doc_analysis_temperature"
                    type="range"
                    min={0}
                    max={2}
                    step={0.1}
                    value={formData.doc_analysis_temperature || 0}
                    onChange={(e) => handleInputChange('doc_analysis_temperature', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotDocAnalysis.temperatureDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="doc_analysis_max_text_length">{t('chatbotDocAnalysis.maxTextLength')}</Label>
                  <Input
                    id="doc_analysis_max_text_length"
                    type="number"
                    min="100"
                    value={formData.doc_analysis_max_text_length || ''}
                    onChange={(e) => handleInputChange('doc_analysis_max_text_length', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotDocAnalysis.maxTextLengthDescription')}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="doc_analysis_system_prompt">{t('chatbotDocAnalysis.systemPrompt')}</Label>
                <Textarea
                  id="doc_analysis_system_prompt"
                  value={formData.doc_analysis_system_prompt || ''}
                  onChange={(e) => handleInputChange('doc_analysis_system_prompt', e.target.value)}
                  placeholder={t('chatbotDocAnalysis.systemPromptPlaceholder')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('chatbotDocAnalysis.systemPromptDescription')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* System Prompt */}
          <Card>
            <CardHeader>
              <CardTitle>{t('chatbotConfig.systemPrompt')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Textarea
                  value={formData.system_prompt || ''}
                  onChange={(e) => handleInputChange('system_prompt', e.target.value)}
                  placeholder={t('chatbotConfig.systemPromptPlaceholder')}
                  rows={8}
                  className="min-h-[200px]"
                />
                <p className="text-xs text-muted-foreground">
                  {t('chatbotConfig.systemPromptDescription')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Default Message */}
          <Card>
            <CardHeader>
              <CardTitle>{t('chatbotConfig.defaultMessage')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Textarea
                  value={formData.default_message || ''}
                  onChange={(e) => handleInputChange('default_message', e.target.value)}
                  placeholder={t('chatbotConfig.defaultMessagePlaceholder')}
                  rows={4}
                  className="min-h-[120px]"
                />
                <p className="text-xs text-muted-foreground">
                  {t('chatbotConfig.defaultMessageDescription')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Conversation Settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t('chatbotConfig.conversationSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="max_conversation_history">{t('chatbotConfig.maxConversationHistory')}</Label>
                  <Input
                    id="max_conversation_history"
                    type="number"
                    min="5"
                    max="100"
                    value={formData.max_conversation_history || ''}
                    onChange={(e) => handleInputChange('max_conversation_history', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.maxConversationHistoryDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="conversation_timeout_hours">{t('chatbotConfig.conversationTimeoutHours')}</Label>
                  <Input
                    id="conversation_timeout_hours"
                    type="number"
                    min="1"
                    max="168"
                    value={formData.conversation_timeout_hours || ''}
                    onChange={(e) => handleInputChange('conversation_timeout_hours', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.conversationTimeoutHoursDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_message_length">{t('chatbotConfig.maxMessageLength')}</Label>
                  <Input
                    id="max_message_length"
                    type="number"
                    min="50"
                    max="5000"
                    value={formData.max_message_length || ''}
                    onChange={(e) => handleInputChange('max_message_length', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.maxMessageLengthDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_audio_duration">{t('chatbotConfig.maxAudioDuration')}</Label>
                  <Input
                    id="max_audio_duration"
                    type="number"
                    min="0"
                    max="600"
                    value={formData.max_audio_duration || ''}
                    onChange={(e) => handleInputChange('max_audio_duration', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.maxAudioDurationDescription')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t('chatbotConfig.statusSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_active">{t('chatbotConfig.active')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.activeDescription')}
                  </p>
                </div>
                <Switch
                  id="is_active"
                  checked={formData.is_active || false}
                  onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_default">{t('chatbotConfig.default')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('chatbotConfig.defaultDescription')}
                  </p>
                </div>
                <Switch
                  id="is_default"
                  checked={formData.is_default || false}
                  onCheckedChange={(checked) => handleInputChange('is_default', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Changes Warning */}
          {hasChanges && (
            <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <p className="text-sm text-orange-600">
                    {t('chatbotConfig.unsavedChanges')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </Main>

      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        open={isResetDialogOpen}
        onOpenChange={setIsResetDialogOpen}
        title={t('chatbotConfig.resetToDefaults')}
        desc={t('chatbotConfig.resetConfirmation')}
        confirmText={t('reset')}
        cancelBtnText={t('cancel')}
        handleConfirm={handleReset}
        destructive={true}
      />
    </div>
  );
}